// src/scenes/GestionStock/MouvementsStockModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { Close, SwapHoriz, Add, Remove, Edit } from '@mui/icons-material';
import { inventoryService } from '../../services/inventory';

const MouvementsStockModal = ({ open, onClose, stock }) => {
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeFiltre, setTypeFiltre] = useState('');

  useEffect(() => {
    if (open && stock) {
      fetchMouvements();
    }
  }, [open, stock, typeFiltre]);

  const fetchMouvements = async () => {
    setLoading(true);
    try {
      const params = {
        succursale_id: stock.succursale,
        produit_id: stock.produit
      };
      
      if (typeFiltre) {
        params.type_mouvement = typeFiltre;
      }

      const response = await inventoryService.getMouvements(params);
      
      if (response.data.results) {
        setMouvements(response.data.results);
      } else {
        setMouvements(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement mouvements:', err);
      setError('Erreur lors du chargement des mouvements');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'entree': return <Add color="success" />;
      case 'sortie': return <Remove color="error" />;
      case 'transfert': return <SwapHoriz color="info" />;
      default: return <Edit color="warning" />;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'entree': return 'Entrée';
      case 'sortie': return 'Sortie';
      case 'transfert': return 'Transfert';
      default: return 'Ajustement';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'entree': return 'success';
      case 'sortie': return 'error';
      case 'transfert': return 'info';
      default: return 'warning';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!stock) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Mouvements de stock
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="subtitle2" color="textSecondary">
          {stock.produit_nom} - {stock.succursale_nom}
        </Typography>
        <Typography variant="caption" display="block" color="textSecondary">
          Stock actuel: <strong>{stock.stock_actuel}</strong>
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtrer par type</InputLabel>
            <Select
              value={typeFiltre}
              label="Filtrer par type"
              onChange={(e) => setTypeFiltre(e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="entree">Entrées</MenuItem>
              <MenuItem value="sortie">Sorties</MenuItem>
              <MenuItem value="transfert">Transferts</MenuItem>
              <MenuItem value="inventaire">Ajustements</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : mouvements.length === 0 ? (
          <Box textAlign="center" p={3}>
            <Typography color="textSecondary">
              Aucun mouvement trouvé
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Quantité</TableCell>
                  <TableCell>Motif</TableCell>
                  <TableCell>Utilisateur</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mouvements.map((mouvement) => (
                  <TableRow key={mouvement.id}>
                    <TableCell>{formatDate(mouvement.date_creation)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getTypeIcon(mouvement.type_mouvement)}
                        label={getTypeLabel(mouvement.type_mouvement)}
                        size="small"
                        color={getTypeColor(mouvement.type_mouvement)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        fontWeight="bold"
                        color={mouvement.type_mouvement === 'entree' ? 'success.main' : 'error.main'}
                      >
                        {mouvement.type_mouvement === 'entree' ? '+' : '-'}
                        {mouvement.quantite}
                      </Typography>
                    </TableCell>
                    <TableCell>{mouvement.motif || '-'}</TableCell>
                    <TableCell>
                      {mouvement.utilisateur_nom || 'Système'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MouvementsStockModal;